import React, { useEffect, useMemo, useState } from "react";
import {
  DownloadOutlined, EditOutlined, VisibilityOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import SectionCard from "../components/SectionCard";
import SubcontractFormDialog from "../components/SubcontractFormDialog";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppButton } from "components/common/buttons";
import { subcontractApi } from "../services/subcontractApi";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { formatCurrencyWithUnit, formatDate } from "utils/formatters";

function InfoField({ label, value }) {
  return <Box className="subcontract-info-field"><label>{label}</label><strong>{value || "—"}</strong></Box>;
}

const ASSIGNEE_SOURCE_LABELS = {
  EXECUTION_GROUP: "Nhóm thực hiện giao khoán",
  DEPARTMENT: "Phòng ban",
  DEPARTMENT_UNIT: "Đội",
};

export default function InformationTab({ contract, personnel = [], documents = [], onChanged }) {
  const [editTab, setEditTab] = useState(null);
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const openEdit = (tab) => setEditTab(tab);
  const closeEdit = () => setEditTab(null);

  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);
  const personnelColumns = useMemo(() => [
    { accessorKey: "displayOrder", header: "STT", size: 50 },
    { accessorKey: "userName", header: "Họ và tên", size: 200 },
    { accessorKey: "title", header: "Chức vụ", size: 100 },
    { accessorKey: "departmentName", header: "Phòng ban", size: 120 },
    { accessorKey: "enterpriseName", header: "Xí nghiệp", size: 200 },
    { accessorKey: "roleInSubcontract", header: "Vai trò trong dự án", size: 140 },
  ], []);
  const documentColumns = useMemo(() => [
    {
      accessorKey: "documentName",
      header: "Tên tài liệu",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    { accessorKey: "documentType", header: "Loại" },
    {
      accessorKey: "description",
      header: "Mô tả",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    {
      id: "uploadDate",
      header: "Ngày tải lên",
      accessorFn: (row) => row.uploadDate || row.createdAt,
      Cell: ({ cell }) => cell.getValue() ? formatDate(cell.getValue()) : "-"
    },
    { accessorKey: "createBy", header: "Người tải" },
  ], []);

  const handlePreviewDocument = (document) => {
    openDocumentPreviewInNewTab({
      file: document,
      isRemote: true,
      downloadFn: subcontractApi.downloadDocument,
      onAlert: setSnackbar,
    });
  };
  const assignee = contract.assignee || {};

  return (
    <Box className="subcontract-stack">
      <SectionCard
        title="Thông tin chung"
        action={<AppButton size="small" startIcon={<EditOutlined />} onClick={() => openEdit(0)}>Cập nhật thông tin</AppButton>}
      >
        <Box className="subcontract-info-grid">
          <InfoField label="Số quyết định giao khoán" value={contract.subcontractNumber} />
          <InfoField label="Tên quyết định giao khoán" value={contract.subcontractName} />
          <InfoField label="Hợp đồng chính" value={[contract.contractNumber, contract.contractName].filter(Boolean).join(" - ")} />
          <InfoField label="Đơn vị thực hiện" value={contract.assigneeName} />
          <InfoField label="Tỉnh/Thành phố" value={contract.provinceName} />
          <InfoField label="Ngày ký" value={formatDate(contract.signDate)} />
          <InfoField label="Ngày bắt đầu" value={formatDate(contract.startDate)} />
          <InfoField label="Ngày kết thúc" value={formatDate(contract.endDate)} />
          <InfoField label="Ngày bắt đầu thực tế" value={formatDate(contract.startDateAct)} />
          <InfoField label="Ngày kết thúc thực tế" value={formatDate(contract.endDateAct)} />
          <InfoField label="Tổng giá trị quyết định giao khoán" value={formatCurrencyWithUnit(contract.totalValue, contract.currency || "VND")} />
          <InfoField label="Đơn vị tiền tệ" value={contract.currency || "VND"} />
          <Box className="subcontract-info-field" sx={{ gridColumn: "1 / -1" }}><label>Mô tả</label><strong>{contract.description || "Chưa có mô tả"}</strong></Box>
        </Box>
      </SectionCard>

      <SectionCard title="Thông tin đơn vị thực hiện">
        <Box className="subcontract-info-grid">
          <InfoField
            label="Loại đơn vị"
            value={ASSIGNEE_SOURCE_LABELS[assignee.assigneeSourceType] || assignee.assigneeSourceType}
          />
          <InfoField label="Tên đơn vị" value={assignee.assigneeName} />
          <InfoField label="Mã đơn vị" value={assignee.assigneeCode} />
          <InfoField label="Người đại diện" value={assignee.leaderName} />
          <InfoField label="Ngày tạo" value={formatDate(assignee.createdAt)} />
          <InfoField label="Ngày cập nhật" value={formatDate(assignee.updatedAt)} />
        </Box>
      </SectionCard>

      <SectionCard
        title="Nhân sự trong quyết định giao khoán"
        action={<AppButton size="small" startIcon={<EditOutlined />} onClick={() => openEdit(1)}>Cập nhật nhân sự</AppButton>}
      >
        <DataTable columns={personnelColumns} data={personnel.filter((item) => item.selected !== false)} />
      </SectionCard>

      <SectionCard
        title="Tài liệu đính kèm"
        action={<AppButton size="small" startIcon={<EditOutlined />} onClick={() => openEdit(2)}>Cập nhật tài liệu</AppButton>}
      >
        <DataTable
          columns={documentColumns}
          data={localDocuments}
          enableRowActions
          renderRowActions={({ row }) => (
            <Box sx={{
              display: "flex",
              gap: 0.5,
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Tooltip title="Xem trước">
                <IconButton size="small" onClick={() => handlePreviewDocument(row.original)}>
                  <VisibilityOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tải xuống">
                <IconButton size="small" onClick={() => subcontractApi.downloadDocumentFile(row.original)}>
                  <DownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </SectionCard>
      <SubcontractFormDialog
        open={editTab !== null}
        onClose={closeEdit}
        initialTab={editTab ?? 0}
        initialData={contract}
        mode="edit"
        personnelData={personnel}
        documentsData={localDocuments}
        onDocumentsChanged={setLocalDocuments}
        onSaved={onChanged}
      />
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
}
